import { Injectable } from "@angular/core";
import { Subject } from "rxjs";

/**
 * Service that emits logout events for state cleanup.
 *
 * This follows the pub/sub pattern to decouple AuthService from application state:
 * - AuthService calls emitLogout() when user logs out
 * - State services subscribe to onLogout$ and clear their cache
 *
 * This way AuthService remains focused on authentication without knowing
 * about all the state services that need clearing on logout.
 *
 * Usage in state services:
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class MyStateService {
 *   constructor() {
 *     inject(LogoutEventService).onLogout$.pipe(
 *       takeUntilDestroyed()
 *     ).subscribe(() => this.clearCache());
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: "root",
})
export class LogoutEventService {
  private readonly _logoutSubject = new Subject<void>();

  /**
   * Guards against emitting multiple logout events for a single dead session.
   * When the refresh token expires, a burst of parallel requests (plus the
   * user/tenant loader) can all detect the failure at once; without this guard
   * each would fire its own signOut + navigation, causing the redirects to race
   * and leave the app on a blank page. Reset via resetLogoutState() once a
   * valid session exists again.
   */
  private _logoutInProgress = false;

  /**
   * Observable that emits when user logs out.
   * Subscribe to this in state services to clear cached data.
   */
  readonly onLogout$ = this._logoutSubject.asObservable();

  /**
   * Emits a logout event to all subscribers.
   * Called by AuthService when user logs out, and by the auth interceptors when
   * the session is detected as expired. Idempotent until resetLogoutState() is
   * called, so a burst of failures only triggers a single logout/redirect.
   */
  emitLogout(): void {
    if (this._logoutInProgress) {
      return;
    }
    this._logoutInProgress = true;
    this._logoutSubject.next();
  }

  /**
   * Clears the logout guard so a subsequent session expiry can trigger a new
   * logout. Called once a valid session is (re)established.
   */
  resetLogoutState(): void {
    this._logoutInProgress = false;
  }
}
